import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/authStore';
import { landingFor } from '@/lib/roleHome';
import { apiGet } from '@/lib/api';
import { isHiringPartnerType } from '@/config/partnerOptions';
import { StudentHomePage } from '@/pages/student/Home';

const PARTNER_ROLES = new Set([
  'partner_admin', 'recruiter', 'hiring_manager', 'partner_accountant', 'technical_panel',
]);

/**
 * Guard that renders the student home ONLY for actual students and student reps;
 * everyone else gets redirected to their role's canonical landing (platform → /platform,
 * TPO → /tpo/today, etc.). Without this, a platform_admin who lands on /home sees the
 * student's "Add a project" card, which is confusing and wrong.
 *
 * For partner-shell roles, we additionally branch on the Partner's TYPE. Non-hiring types
 * (Training_Provider, Internship_Provider, R&D_Lab, NGO, Government_Agency, Placement_Partner,
 * Other) land on /company/coming-soon instead of the campus-hiring dashboard.
 */
export function RoleHomeGate() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);

  const isPartnerUser = !!user && PARTNER_ROLES.has(user.role);
  const partnerId = isPartnerUser ? user?.partner : undefined;
  const partnerQuery = useQuery({
    queryKey: ['company', 'partner', partnerId, 'type'],
    queryFn: () => apiGet<{ partner: { _id: string; type: string } }>(`/group/partners/${partnerId}`),
    enabled: !!partnerId,
    retry: false,
    staleTime: 60_000,
  });

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'student' || user.role === 'student_representative') {
    return <StudentHomePage />;
  }

  if (isPartnerUser) {
    if (partnerQuery.isLoading) return null;
    const partnerType = partnerQuery.data?.partner.type;
    if (partnerType && !isHiringPartnerType(partnerType)) {
      return <Navigate to="/company/coming-soon" replace />;
    }
    return <Navigate to="/company/campus-hiring" replace />;
  }

  return <Navigate to={landingFor(user.role)} replace />;
}
